import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import CarForm from './components/CarForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { Defaults } from '../../../../../modules/API/Defaults/defaults'
import { IFreightCompanyItem } from '../../../../../modules/API/Defaults/models/freightCompany'
import { IWorkerItem } from '../../../../../modules/API/Defaults/models/workers'
import { RootState } from '../../../../../../store/reducers/rootReducer'
import { carTableService } from './services/carTable.service'
import { carTableColumns } from './configs/carTable.columns'
import { carTableFormFields } from './configs/carTable.form'
import { carTableAdapter } from './adapters/carTable.adapter'
import { CarCreatePayload, CarUiRow, CarUpdatePayload } from './types/carTable.types'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const CarTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<CarUiRow, CarCreatePayload, CarUpdatePayload>(
    carTableService,
    carTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<CarUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<CarUiRow>(rows)

  const workerList = useSelector((state: RootState) => state.defaults.workerList) as
    | IWorkerItem[]
    | null

  const [freightCompanies, setFreightCompanies] = useState<IFreightCompanyItem[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await Defaults.getTblFreightCompany('data')
        setFreightCompanies(Array.isArray(res) ? res : [])
      } catch {
        handleNotifications('שגיאה בטעינת הנתונים', { type: 'error' })
      }
    }

    fetchData()
  }, [])

  const freightOptions = useMemo(
    () => [
      { value: 0, label: 'ללא חברת הובלה' },
      ...freightCompanies.map((fc) => ({
        value: fc.FC_ID,
        label: fc.FC_Name?.trim() || String(fc.FC_ID),
      })),
    ],
    [freightCompanies]
  )

  const workerOptions = useMemo(
    () => [
      { value: 0, label: 'ללא עובד' },
      ...(workerList ?? []).map((w) => ({
        value: w.WorkerId,
        label: w.WorkerName?.trim() || String(w.WorkerId),
      })),
    ],
    [workerList]
  )

  const enrichedColumns = useMemo<ColumnDefinition<CarUiRow>[]>(
    () =>
      carTableColumns.map((col) => {
        if (col.key === 'FreightCompanyID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              freightOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        if (col.key === 'DriverWorkerId') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              workerOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        return col
      }),
    [freightOptions, workerOptions]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<CarUiRow>[]>(
    () =>
      carTableFormFields.map((field) => {
        if (field.name === 'FreightCompanyID') {
          return { ...field, getOptions: () => freightOptions }
        }
        if (field.name === 'DriverWorkerId') {
          return { ...field, getOptions: () => workerOptions }
        }
        return field
      }),
    [freightOptions, workerOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<CarUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<CarUiRow> => {
        if (col.key === 'CarID') return { ...col, cellRendererType: 'num' }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'cars', rtl: true })

  const handleFormSubmit = useCallback(
    async (values: Partial<CarUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = carTableAdapter.getRowId(selectedRow)
          const payload = carTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as CarUiRow)
          await updateRow(id, payload)
        } else {
          const payload = carTableAdapter.toCreatePayload(values as CarUiRow)
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle = editingMode === 'edit' ? 'עריכת רכב' : 'הוספת רכב חדש'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={CarForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת רכב"
        message="האם למחוק את הרכב? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(carTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<CarUiRow>
        title="רכבים"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={carTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש רכב…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף רכב"
          />
        }
      />
    </TableSection>
  )
}

export default CarTableModule
